/**
 * ConfirmDialog Component
 * 
 * A confirmation dialog for destructive or important actions.
 * Follows the MO Insights design system aesthetic.
 */

import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    IconButton,
    alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { colors, typography as typographyTokens, radii } from '../../tokens';
import { Button } from '../Button/Button';

export type ConfirmDialogVariant = 'warning' | 'danger' | 'info' | 'success';

export interface ConfirmDialogProps {
    /** Whether the dialog is open */
    open: boolean;
    /** Handler for closing the dialog */
    onClose: () => void;
    /** Handler for confirming the action */
    onConfirm: () => void;
    /** Dialog title */
    title: string;
    /** Dialog message/description */
    message: string | React.ReactNode;
    /** Confirm button text */
    confirmText?: string;
    /** Cancel button text */
    cancelText?: string;
    /** Visual variant */
    variant?: ConfirmDialogVariant;
    /** Whether the confirm action is loading */
    loading?: boolean;
    /** Custom icon to display */
    icon?: React.ReactNode;
}

const variantConfig: Record<ConfirmDialogVariant, { 
    color: string; 
    bgColor: string; 
    icon: React.ReactNode;
    confirmVariant: 'primary' | 'secondary';
}> = {
    warning: {
        color: colors.warning.main,
        bgColor: colors.warning.light,
        icon: <WarningAmberIcon />,
        confirmVariant: 'primary',
    },
    danger: {
        color: colors.error.main,
        bgColor: colors.error.light,
        icon: <ErrorOutlineIcon />,
        confirmVariant: 'primary',
    },
    info: {
        color: colors.info.main,
        bgColor: colors.info.light,
        icon: <InfoOutlinedIcon />,
        confirmVariant: 'primary',
    },
    success: {
        color: colors.success.main,
        bgColor: colors.success.light,
        icon: <CheckCircleOutlineIcon />,
        confirmVariant: 'primary',
    },
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'warning',
    loading = false,
    icon,
}) => {
    const config = variantConfig[variant];
    const displayIcon = icon || config.icon;

    const handleConfirm = () => {
        onConfirm();
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: `${radii.lg}px`,
                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.15)',
                    overflow: 'hidden',
                },
            }}
        >
            {/* Close button */}
            <IconButton
                onClick={onClose}
                disabled={loading}
                sx={{
                    position: 'absolute',
                    right: 12,
                    top: 12,
                    color: colors.neutral[400],
                    '&:hover': {
                        color: colors.neutral[600],
                        backgroundColor: colors.neutral[100],
                    },
                }}
            >
                <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <DialogTitle sx={{ pt: 4, pb: 1, px: 3, textAlign: 'center' }}>
                {/* Icon */}
                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: config.bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        color: config.color,
                        '& svg': {
                            fontSize: 32,
                        },
                    }}
                >
                    {displayIcon}
                </Box>

                <Typography
                    sx={{
                        fontFamily: typographyTokens.fontFamily.display,
                        fontWeight: typographyTokens.fontWeight.bold,
                        fontSize: typographyTokens.fontSize.xl,
                        color: colors.neutral[900],
                    }}
                >
                    {title}
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pb: 2, textAlign: 'center' }}>
                {typeof message === 'string' ? (
                    <Typography
                        sx={{
                            color: colors.neutral[600],
                            fontSize: typographyTokens.fontSize.base,
                            lineHeight: 1.6,
                        }}
                    >
                        {message}
                    </Typography>
                ) : (
                    message
                )}
            </DialogContent>

            <DialogActions 
                sx={{ 
                    px: 3, 
                    pb: 3, 
                    pt: 1,
                    display: 'flex',
                    gap: 1.5,
                    justifyContent: 'center',
                }}
            >
                <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={loading}
                    fullWidth
                >
                    {cancelText}
                </Button>
                <Button
                    variant={config.confirmVariant}
                    onClick={handleConfirm}
                    loading={loading}
                    fullWidth
                    sx={variant === 'danger' ? {
                        backgroundColor: colors.error.main,
                        '&:hover': {
                            backgroundColor: colors.error.dark,
                        },
                    } : undefined}
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmDialog;

